import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth, onAuthStateChanged, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoSDKWrapper from '../components/VideoSDKWrapper';

function fmt(dtStr) {
  try { return new Date(dtStr).toLocaleString(); } catch { return dtStr; }
}

export default function MeetingById() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [user, setUser] = useState(null);
  const [requiresAuth, setRequiresAuth] = useState(false);

  const startMs = useMemo(() => meeting ? Date.parse(meeting.startAt) : null, [meeting]);
  const endMs = useMemo(() => meeting ? Date.parse(meeting.endAt) : null, [meeting]);

  useEffect(() => {
    let unsub = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(unsub);
  }, []);

  // Auth listener
  useEffect(() => {
    const auth = getAuth();
    const off = onAuthStateChanged(auth, (u) => setUser(u));
    return () => off();
  }, []);

  // Handle magic email-link sign-in if present in URL
  useEffect(() => {
    const auth = getAuth();
    const href = window.location.href;
    if (isSignInWithEmailLink(auth, href)) {
      const params = new URLSearchParams(window.location.search);
      const email = params.get('em');
      if (email) {
        signInWithEmailLink(auth, email, href)
          .then(() => {
            // Strip query params to keep clean URL
            window.history.replaceState({}, '', window.location.pathname);
          })
          .catch((e) => {
            console.error('Magic link sign-in failed', e);
          });
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const ref = doc(db, 'meetings', meetingId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError('Meeting not found');
          setLoading(false);
          return;
        }
        setMeeting({ id: snap.id, ...snap.data() });
        setLoading(false);
      } catch (e) {
        const msg = e?.message || '';
        // If Firestore rules require auth, show sign-in prompt instead of error
        if (msg.includes('Missing or insufficient permissions')) {
          setRequiresAuth(true);
          setError(null);
        } else {
          setError(e.message || 'Failed to load meeting');
        }
        setLoading(false);
      }
    })();
  }, [meetingId]);

  // On crossing start time, update status to active once
  useEffect(() => {
    if (!meeting || !startMs) return;
    if (meeting.status !== 'active' && now >= startMs && now < (endMs || Infinity)) {
      const ref = doc(db, 'meetings', meetingId);
      updateDoc(ref, { status: 'active', updatedAt: serverTimestamp() }).catch(() => {});
      setMeeting((m) => ({ ...m, status: 'active' }));
    }
  }, [meeting, meetingId, startMs, endMs, now]);

  // After end, mark completed
  useEffect(() => {
    if (!meeting || !endMs) return;
    if (meeting.status !== 'completed' && now > endMs) {
      const ref = doc(db, 'meetings', meetingId);
      updateDoc(ref, { status: 'completed', updatedAt: serverTimestamp() }).catch(() => {});
      setMeeting((m) => ({ ...m, status: 'completed' }));
    }
  }, [meeting, meetingId, endMs, now]);

  // Attach UID to participant if email matches and uid missing
  useEffect(() => {
    (async () => {
      if (!meeting || !user) return;
      if (!Array.isArray(meeting.participants)) return;
      const email = (user.email || '').toLowerCase();
      if (!email) return;
      // participants may be strings or objects
      let changed = false;
      const updated = meeting.participants.map((p) => {
        if (typeof p === 'string') {
          const pe = p.toLowerCase();
          if (pe === email) {
            changed = true;
            return { email: pe, uid: user.uid, name: user.displayName || '' };
          }
          return p;
        } else if (p && p.email && p.email.toLowerCase() === email && !p.uid) {
          changed = true;
          return { ...p, uid: user.uid, name: user.displayName || p.name || '' };
        }
        return p;
      });
      if (changed) {
        try {
          const ref = doc(db, 'meetings', meetingId);
          await updateDoc(ref, { participants: updated, updatedAt: serverTimestamp() });
          setMeeting((m) => ({ ...m, participants: updated }));
        } catch (e) {
          // non-fatal
        }
      }
    })();
  }, [meeting, user, meetingId]);

  const beforeStart = startMs && now < startMs;
  const afterEnd = endMs && now > endMs;
  const millisToStart = beforeStart && startMs ? Math.max(0, startMs - now) : 0;

  function formatCountdown(ms) {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`;
  }

  // Access control: user must be in participants list (by email match)
  const isAuthorized = React.useMemo(() => {
    if (!meeting) return false;
    if (!Array.isArray(meeting.participants)) return false;
    if (!user || !user.email) return false;
    const email = user.email.toLowerCase();
    return meeting.participants.some((p) => {
      if (typeof p === 'string') return p.toLowerCase() === email;
      if (p && p.email) return p.email.toLowerCase() === email;
      return false;
    });
  }, [meeting, user]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {meeting ? (meeting.subject || 'Video Consultation') : 'Meeting'}
              </h1>
              <p className="text-sm text-gray-500 mb-6">
                {meeting && <>Scheduled: {fmt(meeting.startAt)} - {fmt(meeting.endAt)} · Status: <span className="font-medium">{meeting.status}</span></>}
              </p>

              {loading && (
                <div className="p-4 bg-gray-50 border rounded">Loading meeting…</div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded">{error}</div>
              )}

              {!loading && !error && meeting && (
                <div>
                  {beforeStart && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded">
                      <div>This meeting hasn’t started yet. It will start at <strong>{fmt(meeting.startAt)}</strong>.</div>
                      <div className="mt-1 text-gray-700">Starts in: <span className="font-semibold">{formatCountdown(millisToStart)}</span></div>
                    </div>
                  )}

                  {afterEnd && (
                    <div className="p-4 bg-gray-50 border text-gray-700 rounded">
                      This meeting has ended. Ended at <strong>{fmt(meeting.endAt)}</strong>.
                    </div>
                  )}

                  {!beforeStart && !afterEnd && (
                    user ? (
                      isAuthorized ? (
                        <VideoSDKWrapper meetingId={meeting.id} participantName={user.displayName || 'Guest'} />
                      ) : (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                          You are not authorized to join this meeting with this account.
                        </div>
                      )
                    ) : (
                      <div className="relative">
                        <div className="p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded mb-4">
                          {requiresAuth ? 'Please sign in to view and join this meeting.' : 'Please sign in to join this meeting.'}
                        </div>
                        <button
                          className="px-4 py-2 rounded bg-blue-600 text-white"
                          onClick={() => navigate(`/signup?redirect=${encodeURIComponent(location.pathname)}`)}
                        >
                          Sign in to Continue
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
